import React, { useState } from 'react';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import RecommendationCard from './RecommendationCard';
import RecommendationDialog from './RecommendationDialog';
import { PublishedRecommendation } from './site-recommendation.service';
import useRecommendationCarousel from './useRecommendationCarousel';

interface RecommendationCarouselProps {
  recommendations: PublishedRecommendation[];
  label: string;
}

const DESKTOP_CAROUSEL_MINIMUM = 4;

const RecommendationCarousel: React.FC<RecommendationCarouselProps> = ({
  recommendations,
  label,
}) => {
  const [selectedRecommendation, setSelectedRecommendation] =
    useState<PublishedRecommendation | null>(null);
  const { activeIndex, goTo, trackRef, updateActiveIndex, visibleCount } =
    useRecommendationCarousel(recommendations.length);

  const firstVisible = activeIndex + 1;
  const lastVisible = Math.min(
    recommendations.length,
    activeIndex + visibleCount
  );
  const hasPrevious = activeIndex > 0;
  const hasNext = lastVisible < recommendations.length;
  const desktopCarousel = recommendations.length >= DESKTOP_CAROUSEL_MINIMUM;
  const mobileCarousel = recommendations.length > 1;

  return (
    <>
      <div
        className={[
          'home-recommendation-carousel',
          desktopCarousel ? 'home-recommendation-carousel--desktop-active' : '',
          mobileCarousel ? 'home-recommendation-carousel--mobile-active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="region"
        aria-roledescription={mobileCarousel ? 'carousel' : undefined}
        aria-label={label}
      >
        <div
          ref={trackRef}
          className={`home-recommendation-carousel__track recommendation-card-grid recommendation-card-grid--${Math.min(
            recommendations.length,
            3
          )}`}
          onScroll={updateActiveIndex}
        >
          {recommendations.map((recommendation, index) => (
            <div
              key={recommendation.id}
              className="home-recommendation-carousel__slide home-recommendation-reveal"
              data-home-reveal
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${recommendations.length}`}
            >
              <RecommendationCard
                recommendation={recommendation}
                onReadFull={setSelectedRecommendation}
              />
            </div>
          ))}
        </div>

        {mobileCarousel ? (
          <div className="home-recommendation-carousel__controls">
            <p aria-live="polite">
              <span className="sr-only">Showing recommendations </span>
              {firstVisible}–{lastVisible} of {recommendations.length}
            </p>
            <div>
              <button
                type="button"
                onClick={() => goTo(-1)}
                disabled={!hasPrevious}
                aria-label="Previous recommendations"
              >
                <ArrowLeftOutlined aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => goTo(1)}
                disabled={!hasNext}
                aria-label="Next recommendations"
              >
                <ArrowRightOutlined aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <RecommendationDialog
        recommendation={selectedRecommendation}
        onClose={() => setSelectedRecommendation(null)}
      />
    </>
  );
};

export default RecommendationCarousel;
